/**
 * « THÈME DE L'ALBUM » — ONE bar, three places (owner, 2026-10-14: « this should have a
 * better UI and also this needs to be in the editor interface for each album so each album
 * has different settings — make it like a bar »):
 *
 *   · the album EDITOR (staff with settings.manage) — the album being edited, each album of
 *     a Duo/Trio its own; « Appliquer : Régénérer » reruns the layout with the new settings;
 *   · the admin ORDER page — the same bar, per album;
 *   · the admin « Modèles des thèmes » gallery header — the same pieces (theme tabs, coloris
 *     dots, steppers) for the GLOBAL defaults.
 *
 * Per album (album_sessions, PATCH /settings/album-theme-bars/:id — settings.manage):
 *   theme           theme_override (migration 248): a theme picked for THIS album — any, even
 *                   inactive — « Sans thème », or Auto = the cover's categories pick it;
 *   coloris         theme_colourway (migration 244): Auto = picked from the cover, or one of
 *                   the theme's colourways;
 *   couleur         theme_accent_override (migration 246): the tint of the theme's pieces;
 *                   reset = the cover's colour;
 *   % / modèles     theme_model_pct / theme_model_variety: empty = the global setting
 *                   (« par défaut : 30 % »), else the theme's own default.
 * Saved as soon as it changes (debounced), each column on its own call — before its migration
 * only that one control is greyed, the rest still saves. Applied at the next layout.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { settingsApi, type AlbumThemeInfo } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useT } from '@/lib/i18n';

// ─── Shared pieces (same visual language everywhere) ──────────────────────────

export type SaveState = 'idle' | 'saving' | 'saved' | 'error';

/** The bar's frame: one row on desktop, wraps on a phone. */
export function BarShell({ children, className, ...rest }: { children: ReactNode; className?: string } & Record<`data-${string}`, string | undefined>) {
  return (
    <div {...rest} className={cn('flex flex-wrap items-center gap-x-2.5 gap-y-2 rounded-xl border border-adm-line bg-adm-surface px-3 py-2 text-xs text-adm-ink-2 shadow-sm', className)}>
      {children}
    </div>
  );
}

/** A labelled control inside the bar. */
export function BarGroup({ label, children, title, className }: { label: ReactNode; children: ReactNode; title?: string; className?: string }) {
  return (
    <div className={cn('flex items-center gap-1.5 min-w-0', className)} title={title}>
      {label != null && <span className="text-[11px] font-medium text-adm-muted whitespace-nowrap">{label}</span>}
      {children}
    </div>
  );
}

/** A thin vertical divider between groups (desktop only — on a phone the rows wrap). */
export function BarDivider() {
  return <span aria-hidden className="hidden lg:block h-5 w-px bg-adm-line" />;
}

/** Active (green) / inactive (grey) theme dot. */
export function ThemeDot({ on, className }: { on: boolean; className?: string }) {
  return <span aria-hidden className={cn('inline-block w-1.5 h-1.5 rounded-full shrink-0', on ? 'bg-emerald-500' : 'bg-gray-300', className)} />;
}

/** −  [value | default]  +   — empty = the default (shown greyed). */
export function Stepper({ value, defaultValue, onChange, min, max, step = 1, unit, label, disabled }: {
  value: number | null;
  defaultValue: number;
  onChange: (v: number | null) => void;
  min: number; max: number; step?: number; unit?: string; label: string; disabled?: boolean;
}) {
  const { t } = useT();
  const [text, setText] = useState(value == null ? '' : String(value));
  useEffect(() => { setText(value == null ? '' : String(value)); }, [value]);
  const clamp = (n: number) => Math.min(max, Math.max(min, Math.round(n)));
  const bump = (d: number) => onChange(clamp((value ?? defaultValue) + d));
  const commit = (s: string) => {
    const v = s.trim() === '' ? null : clamp(Number(s));
    setText(v == null ? '' : String(v));
    if (v !== value) onChange(v);
  };
  const btn = 'w-6 h-6 flex items-center justify-center rounded-md text-adm-muted hover:bg-adm-surface-3 hover:text-adm-ink disabled:opacity-40 disabled:hover:bg-transparent';
  return (
    <span className="inline-flex items-center gap-1" data-stepper={label}
      // The range is said, not discovered by a refused save (« Modèles différents » stops at 20 — app_settings check).
      title={`${value == null ? `${t('par défaut')} : ${defaultValue}${unit ? ` ${unit}` : ''} · ` : ''}${t('de {a} à {b}', { a: String(min), b: `${max}${unit ? ` ${unit}` : ''}` })}`}>
      <span className={cn('inline-flex items-center rounded-lg border border-adm-line bg-adm-surface-2 p-0.5', disabled && 'opacity-50')}>
        <button type="button" className={btn} aria-label={`${label} −`} disabled={disabled || (value ?? defaultValue) <= min} onClick={() => bump(-step)}>−</button>
        <input
          aria-label={label}
          inputMode="numeric"
          disabled={disabled}
          className={cn('w-9 bg-transparent text-center text-sm tabular-nums outline-none', value == null ? 'text-adm-faint' : 'text-adm-ink font-semibold')}
          placeholder={String(defaultValue)}
          value={text}
          onChange={(e) => setText(e.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
          onBlur={(e) => commit(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        />
        <button type="button" className={btn} aria-label={`${label} +`} disabled={disabled || (value ?? defaultValue) >= max} onClick={() => bump(step)}>+</button>
      </span>
      {unit && <span className="text-adm-muted">{unit}</span>}
      {value != null ? (
        <button type="button" className="text-[11px] text-adm-muted underline decoration-dotted hover:text-adm-ink" title={t('Revenir au réglage par défaut ({n})', { n: `${defaultValue}${unit ? ` ${unit}` : ''}` })}
          aria-label={`${label} : ${t('par défaut')}`} onClick={() => onChange(null)}>↺</button>
      ) : null}
    </span>
  );
}

export interface ColourwayDot { id: string; name: string; paper?: string; accent?: string }

/** A colourway's dot: its paper, cut by its accent. */
export function Swatch({ c, size = 20 }: { c: Pick<ColourwayDot, 'paper' | 'accent'>; size?: number }) {
  const paper = c.paper ?? '#F3EEE4', accent = c.accent ?? '#C9A04C';
  return <span aria-hidden className="inline-block rounded-full border border-black/10 shrink-0" style={{ width: size, height: size, background: `linear-gradient(135deg, ${paper} 52%, ${accent} 52%)` }} />;
}

/**
 * The colourways as dots. `auto` adds an « Auto » first dot (the cover's pick, value null).
 * `labels`: every chip shows its name (the gallery); else only the chosen one does (the bar).
 */
export function ColourwayDots({ items, value, onChange, auto, labels, disabled }: {
  items: ColourwayDot[];
  value: string | null;
  onChange: (id: string | null) => void;
  auto?: ColourwayDot | null;
  labels?: boolean;
  disabled?: boolean;
}) {
  const { t } = useT();
  const current = value == null ? null : items.find((c) => c.id === value);
  const chip = (active: boolean) => cn('inline-flex items-center gap-1.5 rounded-full border transition-colors disabled:opacity-50',
    labels ? 'pl-1 pr-2.5 py-0.5 text-sm' : 'p-0.5',
    active ? 'border-brand-500 ring-1 ring-brand-500 bg-adm-surface text-adm-ink' : 'border-adm-line bg-adm-surface text-adm-muted hover:bg-adm-surface-3');
  return (
    <span className="inline-flex items-center gap-1 flex-wrap" role="radiogroup" data-colourways>
      {auto !== undefined && (
        <button type="button" role="radio" aria-checked={value == null} disabled={disabled} className={chip(value == null)}
          title={auto ? t('Auto — choisi d’après la couverture : {n}', { n: t(auto.name) }) : t('Auto — choisi d’après la couverture')} onClick={() => onChange(null)}>
          <span className="relative inline-flex">
            {auto ? <Swatch c={auto} /> : <span className="w-5 h-5 rounded-full border border-dashed border-adm-line-strong" />}
            <span className="absolute -bottom-0.5 -right-0.5 rounded-full bg-adm-ink text-adm-surface text-[8px] font-bold leading-none px-[3px] py-[1px]">A</span>
          </span>
          {labels && t('Auto')}
        </button>
      )}
      {items.map((c) => (
        <button key={c.id} type="button" role="radio" aria-checked={value === c.id} aria-label={t(c.name)} title={t(c.name)} disabled={disabled}
          className={chip(value === c.id)} onClick={() => onChange(c.id)}>
          <Swatch c={c} />
          {labels && t(c.name)}
        </button>
      ))}
      {!labels && (
        <span className="ml-1 text-adm-ink-2 whitespace-nowrap lg:max-[1399px]:hidden">
          {current ? t(current.name) : auto ? `${t('Auto')} · ${t(auto.name)}` : t('Auto')}
        </span>
      )}
    </span>
  );
}

/** « Couleur du thème »: a round swatch over a colour input; reset = `resetLabel` (the cover's colour). */
export function AccentPicker({ value, fallback, onChange, disabled, resetLabel, title }: {
  value: string | null;
  fallback: string;
  onChange: (hex: string | null) => void;
  disabled?: boolean;
  resetLabel: string;
  title?: string;
}) {
  const { t } = useT();
  const shown = value ?? fallback;
  return (
    <span className="inline-flex items-center gap-1.5" data-accent title={title}>
      <label className={cn('relative inline-flex w-6 h-6 rounded-full border border-black/15 shadow-inner cursor-pointer', value == null && 'ring-1 ring-dashed ring-adm-line-strong ring-offset-1', disabled && 'opacity-50 cursor-not-allowed')}
        style={{ background: shown }}>
        <input type="color" aria-label={t('Couleur du thème')} disabled={disabled} className="absolute inset-0 opacity-0 cursor-pointer disabled:cursor-not-allowed"
          value={shown} onChange={(e) => onChange(e.target.value.toUpperCase())} />
      </label>
      {value ? (
        <button type="button" className="text-[11px] text-adm-muted underline decoration-dotted hover:text-adm-ink whitespace-nowrap" onClick={() => onChange(null)} disabled={disabled}>
          ↺ {resetLabel}
        </button>
      ) : <span className="text-[11px] text-adm-faint whitespace-nowrap">{resetLabel}</span>}
    </span>
  );
}

/** Saving / saved / failed — small, right after the controls. */
export function SaveBadge({ state, onRetry, savedLabel }: { state: SaveState; onRetry?: () => void; savedLabel?: string }) {
  const { t } = useT();
  if (state === 'idle') return null;
  if (state === 'saving') return <span className="inline-flex items-center gap-1 text-adm-muted whitespace-nowrap" data-save-state="saving"><span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />{t('Enregistrement…')}</span>;
  if (state === 'saved') return <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400 whitespace-nowrap" data-save-state="saved">✓ {savedLabel ?? t('Enregistré')}</span>;
  return (
    <button type="button" className="text-red-700 dark:text-red-400 underline whitespace-nowrap" data-save-state="error" onClick={onRetry}>
      {t('Échec — réessayer')}
    </button>
  );
}

/** The gallery's theme tabs: a segmented bar, each with its active/inactive dot. */
export function ThemeTabs({ themes, value, onChange }: { themes: Array<{ slug: string; name: string; on: boolean }>; value: string; onChange: (slug: string) => void }) {
  const { t } = useT();
  return (
    <div role="tablist" className="flex gap-0.5 overflow-x-auto rounded-lg bg-adm-surface-3 p-0.5 max-w-full" data-theme-tabs>
      {themes.map((x) => (
        <button key={x.slug} type="button" role="tab" aria-selected={x.slug === value} onClick={() => onChange(x.slug)}
          title={x.on ? t('Actif') : t('Inactif — aucun album ne le reçoit')}
          className={cn('inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm whitespace-nowrap transition-colors',
            x.slug === value ? 'bg-adm-surface text-adm-ink font-semibold shadow-sm' : x.on ? 'text-adm-ink-2 hover:bg-adm-surface/60' : 'text-adm-faint hover:bg-adm-surface/60')}>
          <ThemeDot on={x.on} />
          {t(x.name)}
          {!x.on && <span className="sr-only"> · {t('inactif')}</span>}
        </button>
      ))}
    </div>
  );
}

/** Debounced autosave: `push(patch)` merges and sends after `ms`; state for the badge. */
export function useAutosave<P extends object>(send: (patch: P) => Promise<void>, ms = 700) {
  const [state, setState] = useState<SaveState>('idle');
  const pending = useRef<Partial<P>>({});
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sendRef = useRef(send);
  sendRef.current = send;
  const flush = useCallback(async () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    const patch = pending.current as P;
    if (!Object.keys(patch).length) return;
    pending.current = {};
    setState('saving');
    try { await sendRef.current(patch); setState('saved'); }
    catch { pending.current = { ...patch, ...pending.current }; setState('error'); }
  }, []);
  const push = useCallback((patch: Partial<P>) => {
    pending.current = { ...pending.current, ...patch };
    setState('saving');
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { void flush(); }, ms);
  }, [flush, ms]);
  // leaving the page mid-wait still saves
  useEffect(() => () => { void flush(); }, [flush]);
  return { state, push, flush };
}

// ─── Per album: the bar itself ────────────────────────────────────────────────

export const AUTO_THEME = '';
const forbidden = (e: unknown) => [401, 403].includes(Number((e as { status?: number } | null)?.status));
const NONE = 'none';

interface Draft { theme: string | null; colourway: string | null; accent: string | null; pct: number | null; variety: number | null }

/**
 * « Thème de l'album » for one album — editor + order page. `onRegenerate` (editor): shown as
 * « Appliquer : Régénérer » once something changed, to rerun this album's layout.
 */
const OPEN_KEY = 'storiz_theme_bar_open';
function initiallyOpen(): boolean {
  try { const v = localStorage.getItem(OPEN_KEY); if (v === '1' || v === '0') return v === '1'; } catch { /* ignore */ }
  return typeof window === 'undefined' || window.innerWidth >= 768;
}

export function AlbumThemeBar({ sessionId, onRegenerate, regenerating, debounceMs = 700, className, title, collapsible }: {
  sessionId: string;
  /** Editor: a one-line summary that opens the bar (closed by default on a phone; the choice is remembered). */
  collapsible?: boolean;
  onRegenerate?: () => void;
  regenerating?: boolean;
  debounceMs?: number;
  className?: string;
  title?: ReactNode;
}) {
  const { t } = useT();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['album-theme', sessionId], queryFn: () => settingsApi.albumTheme(sessionId), staleTime: 60_000,
    retry: (n, e) => !forbidden(e) && n < 2,
  });
  const info = q.data;
  const [draft, setDraft] = useState<Draft | null>(null);
  const [changed, setChanged] = useState(false);
  const [open, setOpenState] = useState(() => !collapsible || initiallyOpen());
  const setOpen = (v: boolean) => { setOpenState(v); try { localStorage.setItem(OPEN_KEY, v ? '1' : '0'); } catch { /* ignore */ } };
  // the album's saved values, seeded once per album (a refetch after a save never undoes a newer edit)
  const seeded = useRef<string | null>(null);
  useEffect(() => {
    if (!info || seeded.current === sessionId) return;
    seeded.current = sessionId;
    setDraft({ theme: info.chosen ?? null, colourway: info.saved ?? null, accent: info.accent ?? null, pct: info.pct ?? null, variety: info.variety ?? null });
    setChanged(false);
  }, [info, sessionId]);

  type Patch = { pct?: number | null; variety?: number | null; colourway?: string | null; accent?: string | null; theme?: string | null };
  const { state, push, flush } = useAutosave<Patch>(async (p) => {
    // each column on its own call: before its migration only that one fails
    if ('theme' in p) await settingsApi.albumThemeBars(sessionId, { theme: p.theme ?? null });
    if ('pct' in p || 'variety' in p) {
      const bars: Patch = {};
      if ('pct' in p) bars.pct = p.pct ?? null;
      if ('variety' in p) bars.variety = p.variety ?? null;
      await settingsApi.albumThemeBars(sessionId, bars);
    }
    if ('colourway' in p) await settingsApi.albumThemeBars(sessionId, { colourway: p.colourway ?? null });
    if ('accent' in p) await settingsApi.albumThemeBars(sessionId, { accent: p.accent ?? null });
    setChanged(true);
    await qc.invalidateQueries({ queryKey: ['album-theme', sessionId] });
  }, debounceMs);

  const set = (patch: Partial<Draft>, send: Patch = patch) => {
    setDraft((d) => (d ? { ...d, ...patch } : d));
    push(send);
  };

  const themes = info?.themes ?? [];
  const themeName = (slug: string | null | undefined) => t(themes.find((x) => x.slug === slug)?.name ?? (slug ?? ''));
  // « Auto · none » read as « not detected » (2026-10-17): say which theme the cover names when it is off.
  const autoName = (i: AlbumThemeInfo) => i.coverTheme ? themeName(i.coverTheme)
    : i.coverNamedTheme ? `${themeName(i.coverNamedTheme)} (${t('inactif')})` : t('aucun');
  const autoCw = useMemo(() => info?.colourways.find((c) => c.id === info.auto) ?? null, [info]);
  // will this album actually get a theme at its next layout? (the dot)
  const getsTheme = !!info && !!draft && info.themesSwitch !== false && draft.theme !== NONE && (!!draft.theme || !!info.coverTheme);
  const noTheme = draft?.theme === NONE;

  // not allowed to change the album's theme (settings.manage): no bar at all
  if (q.isError && forbidden(q.error)) return null;
  if (q.isError) {
    return <BarShell className={className} data-album-theme-bar={sessionId}><span className="text-adm-muted">{t('Thème de l’album indisponible')}</span></BarShell>;
  }
  if (!info || !draft) {
    return <BarShell className={cn('min-h-[44px]', className)} data-album-theme-bar={sessionId}><span className="text-adm-faint">{t('Thème de l’album…')}</span></BarShell>;
  }

  const heading = <span className="text-[13px] font-semibold text-adm-ink whitespace-nowrap">{title ?? t('Thème de l’album')}</span>;
  if (collapsible && !open) {
    const cw = info.colourways.find((c) => c.id === draft.colourway);
    const summary = [
      draft.theme === NONE ? t('Sans thème') : draft.theme ? themeName(draft.theme) : `${t('Auto')} · ${autoName(info)}`,
      cw ? t(cw.name) : `${t('coloris')} ${t('Auto').toLowerCase()}`,
      `${draft.pct ?? info.defaultPct ?? 25} %`,
      `${draft.variety ?? info.defaultVariety ?? 4} ${t('modèles')}`,
    ];
    return (
      <BarShell className={className} data-album-theme-bar={sessionId} data-collapsed="true">
        <button type="button" onClick={() => setOpen(true)} aria-expanded={false} className="flex items-center gap-2 min-w-0 flex-1 text-left">
          <ThemeDot on={getsTheme} />
          {heading}
          <span className="truncate text-adm-muted">{summary.join(' · ')}</span>
          <span className="ml-auto shrink-0 text-adm-ink-2 font-medium">{t('Modifier')} ▾</span>
        </button>
        <SaveBadge state={state} onRetry={() => void flush()} />
      </BarShell>
    );
  }

  return (
    <BarShell className={className} data-album-theme-bar={sessionId}>
      {heading}

      <BarGroup label={null} title={info.themeReady === false ? t('Migration 248 pas encore appliquée') : t('Le thème de cet album (Auto = celui de la couverture)')}>
        <span className="inline-flex items-center gap-1.5 rounded-lg border border-adm-line bg-adm-surface-2 pl-2 pr-1">
          <ThemeDot on={getsTheme} />
          <select
            aria-label={t('Thème de l’album')}
            className="bg-transparent py-1 pr-1 text-sm text-adm-ink outline-none disabled:opacity-50 w-[8.5rem]"
            disabled={info.themeReady === false}
            value={draft.theme ?? AUTO_THEME}
            onChange={(e) => {
              const v = e.target.value === AUTO_THEME ? null : e.target.value;
              // a new theme: its own colourways — back to Auto
              set({ theme: v, colourway: null }, info.ready && draft.colourway != null ? { theme: v, colourway: null } : { theme: v });
            }}
          >
            <option value={AUTO_THEME}>{t('Auto')} · {autoName(info)}</option>
            {themes.map((x) => <option key={x.slug} value={x.slug}>{themeName(x.slug)}{x.on ? '' : ` · ${t('inactif')}`}</option>)}
            <option value={NONE}>{t('Sans thème')}</option>
          </select>
        </span>
      </BarGroup>

      <BarDivider />
      <BarGroup label={t('Coloris')} title={info.ready ? undefined : t('Migration 244 pas encore appliquée')}>
        <ColourwayDots items={info.colourways} value={draft.colourway} auto={autoCw} disabled={!info.ready || noTheme}
          onChange={(id) => set({ colourway: id })} />
      </BarGroup>

      <BarDivider />
      <BarGroup label={t('Couleur')}>
        <AccentPicker value={draft.accent} fallback={info.defaultAccent ?? '#E2725B'} disabled={!info.accentReady || noTheme}
          resetLabel={t('couverture')}
          title={info.accentReady ? t('Teinte les lettres découpées, rubans, washi, pierres et papier du thème') : t('Migration 246 pas encore appliquée')}
          onChange={(hex) => set({ accent: hex })} />
      </BarGroup>

      <BarDivider />
      <BarGroup label={t('Pages avec modèle')}>
        <Stepper label={t('Pages avec modèle')} value={draft.pct} defaultValue={info.defaultPct ?? 25} min={0} max={100} step={5} unit="%"
          disabled={noTheme} onChange={(v) => set({ pct: v })} />
      </BarGroup>
      <BarGroup label={t('Modèles différents')}>
        <Stepper label={t('Modèles différents')} value={draft.variety} defaultValue={info.defaultVariety ?? 4} min={1} max={50}
          disabled={noTheme} onChange={(v) => set({ variety: v })} />
      </BarGroup>

      <span className="flex items-center gap-2 ml-auto">
        <SaveBadge state={state} onRetry={() => void flush()} />
        {onRegenerate ? (
          <button type="button" onClick={async () => { await flush(); setChanged(false); onRegenerate(); }} disabled={regenerating}
            data-theme-regenerate
            title={t('Relancer la mise en page de cet album avec ces réglages')}
            className={cn('inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-semibold whitespace-nowrap transition-colors disabled:opacity-50',
              changed ? 'bg-brand-500 text-white hover:bg-brand-600' : 'border border-adm-line text-adm-ink-2 hover:bg-adm-surface-3')}>
            {regenerating ? t('Régénération…') : <>✨ <span className="hidden min-[1400px]:inline">{t('Appliquer :')} </span>{t('Régénérer')}</>}
          </button>
        ) : (
          (changed || state === 'saved') && <span className="text-[11px] text-adm-muted whitespace-nowrap">{t('à la prochaine mise en page')}</span>
        )}
        {collapsible && (
          <button type="button" onClick={() => setOpen(false)} aria-expanded aria-label={t('Réduire')} data-collapse title={t('Réduire')}
            className="w-6 h-6 flex items-center justify-center rounded-md text-adm-muted hover:bg-adm-surface-3">▴</button>
        )}
      </span>
      {info.themesSwitch === false && (
        <span className="basis-full text-[11px] text-amber-800 dark:text-amber-300">{t('Thèmes désactivés (interrupteur général) : aucun album ne reçoit de thème pour l’instant.')}</span>
      )}
    </BarShell>
  );
}

// ─── Global (gallery header): the two defaults, autosaved ─────────────────────

export function GlobalThemeSteppers({ pct, variety, fallbackPct = 25, fallbackVariety = 4, debounceMs = 700 }: {
  pct: number | null; variety: number | null; fallbackPct?: number; fallbackVariety?: number; debounceMs?: number;
}) {
  const { t } = useT();
  const qc = useQueryClient();
  const [p, setP] = useState(pct), [v, setV] = useState(variety);
  useEffect(() => { setP(pct); }, [pct]);
  useEffect(() => { setV(variety); }, [variety]);
  const { state, push, flush } = useAutosave<{ theme_model_pct?: number | null; theme_model_variety?: number | null }>(async (patch) => {
    await settingsApi.update(patch as never);
    await qc.invalidateQueries({ queryKey: ['settings'] });
  }, debounceMs);
  return (
    <span className="flex flex-wrap items-center gap-x-3 gap-y-2" data-theme-bars="global">
      <BarGroup label={t('Pages avec modèle')}>
        <Stepper label={t('Pages avec modèle')} value={p} defaultValue={fallbackPct} min={0} max={100} step={5} unit="%"
          onChange={(x) => { setP(x); push({ theme_model_pct: x }); }} />
      </BarGroup>
      <BarGroup label={t('Modèles différents')}>
        <Stepper label={t('Modèles différents')} value={v} defaultValue={fallbackVariety} min={1} max={50}
          onChange={(x) => { setV(x); push({ theme_model_variety: x }); }} />
      </BarGroup>
      <SaveBadge state={state} onRetry={() => void flush()} savedLabel={t('Enregistré — à la prochaine mise en page')} />
    </span>
  );
}

export type { AlbumThemeInfo };
